/**
 * AnalysisSection - Scenario Analysis Tab Content
 *
 * Extracts the scenario analysis configuration from ScenarioEditor Tab 5.
 * Contains the ScenarioAnalysis component for comparing scenarios.
 */

import React, { useMemo } from 'react';
import ScenarioAnalysis from '../../../components/ScenarioAnalysis';
import { debug } from '@vortx/shared-ui';

/**
 * AnalysisSection Component
 *
 * Main component for Tab 5 (Scenario Analysis) content.
 *
 * @param {Object} props
 * @param {string} props.projectId - Current project ID
 * @param {string} props.scenarioId - Current scenario ID
 * @param {Object} props.scenarioForm - Scenario form hook state
 * @param {Object} props.optimization - Optimization hook state
 * @param {Function} props.buildScenarioPayload - Function to build scenario payload
 * @param {Object} props.budget - Budget hook state
 * @param {Object} props.commercialAssumptions - Commercial assumptions hook state
 * @param {Object} props.itcSettings - ITC settings for analysis
 * @param {Array} props.mainLoadProfile - Load profile data (8760 array)
 * @param {Object} props.monthlyPerformanceData - Monthly performance data
 * @param {Object} props.adminCapexRates - Admin-managed CapEx rates
 */
function AnalysisSection({
    projectId,
    scenarioId,
    scenarioForm,
    optimization,
    buildScenarioPayload,
    budget,
    commercialAssumptions,
    itcSettings,
    mainLoadProfile,
    monthlyPerformanceData,
    adminCapexRates,
}) {
    // Build current scenario data with reopt_inputs
    const currentScenarioData = useMemo(() => {
        const payload = buildScenarioPayload();

        // DEBUG: Log what we have in optimization.results
        debug.log('[ScenarioAnalysis] Checking optimization.results:', {
            hasResults: !!optimization.results,
            hasInputs: !!optimization.results?.inputs,
            hasOutputs: !!optimization.results?.outputs,
            resultsKeys: optimization.results ? Object.keys(optimization.results) : [],
            checkPaths: {
                'results.inputs': optimization.results?.inputs ? Object.keys(optimization.results.inputs) : null,
                'results.outputs': optimization.results?.outputs ? Object.keys(optimization.results.outputs).slice(0, 10) : null,
                'results.status': optimization.results?.status,
            }
        });

        debug.log('[ScenarioAnalysis] Using buildScenarioPayload for matrix builder');

        return {
            ...scenarioForm.serializeFormState(),
            last_run_results: optimization.results,
            reopt_inputs: payload,
        };
    }, [scenarioForm, optimization.results, buildScenarioPayload]);

    return (
        <ScenarioAnalysis
            projectId={projectId}
            currentScenarioId={scenarioId}
            currentScenarioData={currentScenarioData}
            budgetRates={budget.capexInputs}
            budget={budget}
            commercialAssumptions={commercialAssumptions}
            itcSettings={itcSettings}
            mainLoadProfile={mainLoadProfile}
            monthlyPerformanceData={monthlyPerformanceData}
            gasTariffData={scenarioForm.gasTariffData}
            electricTariffData={scenarioForm.customTariffData || scenarioForm.selectedTariffData}
            adminCapexRates={adminCapexRates}
        />
    );
}

export default AnalysisSection;
